import { IconButton } from '@material-ui/core';
import { ArrowBack, ArrowForward } from '@material-ui/icons';
import NotificationsIcon from '@material-ui/icons/Notifications';
import type { LaunchPadData, PublicCollectionData } from '@mogul/api-client';
import { LaunchpadCountDown } from '@mogul/shared';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import circleIcon from '../../common/svg/dashboard/circle-icon.svg';
import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';
import { SignupModal } from '../common/modal/SignupDropNotifyModal.component';
import styles from './DashboardCarousel.module.css';
import { MogiesCarouselSlide } from './MogiesCarouselSlide.component';
import { ProjectCarouselSlide } from './ProjectCarouselSlide.component';
import { StakingCarouselSlide } from './StakingCarouselSlide.component';
import { VotingCarouselSlide } from './VotingCarouselSlide.component';

export interface ProjectMetadata {
  description: string;
  genre: string;
  image: string;
  name: string;
  studio: string;
}

export interface VotingDataProps {
  images: string[];
  estimatedEndDate: Date;
  isVotingEnded?: boolean;
  roundStarted: boolean;
  roundEnded: boolean;
}

export interface DashboardCarouselProps {
  launchpadData: LaunchPadData[];
  collectionData: PublicCollectionData[];
  votingData: VotingDataProps;
}

export const DashboardCarousel: React.FC<DashboardCarouselProps> = props => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const history = useHistory();

  const currentSlideRef = useRef(0);
  const pauseSlideRef = useRef(false);

  const { setModal } = useContext(ModalStateContext);

  const countdownHeader = props.votingData.roundEnded
    ? 'VOTING ENDED'
    : props.votingData.roundStarted
    ? 'ESTIMATED TIME LEFT'
    : 'VOTING STARTS IN';

  const goToCollection = (index: number, isCollection = false) => {
    if (!isCollection) {
      const network =
        props.launchpadData[index].collection?.network === 'MAINNET' ||
        props.launchpadData[index].collection?.network === 'GOERLI' ||
        props.launchpadData[index].collection?.network === 'RINKEBY'
          ? 'eth'
          : 'bsc';
      history.push(
        `/platform/collection/${network}/${props.launchpadData[index].collection?.slug}`,
      );
    } else {
      const network =
        props.collectionData[index].network === 'MAINNET' ||
        props.collectionData[index].network === 'GOERLI' ||
        props.collectionData[index].network === 'RINKEBY'
          ? 'eth'
          : 'bsc';
      history.push(
        `/platform/collection/${network}/${props.collectionData[index].slug}`,
      );
    }
  };

  const goToLaunchpad = (index: number) => {
    history.push(
      `/platform/showcase/${props.launchpadData[index].collection?.slug}`,
    );
  };

  const goToMarketplace = () => {
    history.push('/platform/marketplace');
  };

  const goToSlide = (index: number) => {
    currentSlideRef.current = index;
    pauseSlideRef.current = true;
    setTimeout(function () {
      pauseSlideRef.current = false;
    }, 8000);
    setCurrentSlide(index);
  };

  useEffect(() => {
    let touchstartX = 0;
    let touchendX = 0;

    const carouselContainer = document.getElementById('carouselContainer');

    const handleGesture = () => {
      if (touchendX < touchstartX - 150) {
        // + 3 compensates for hard coded two slide
        if (currentSlideRef.current + 1 < props.launchpadData.length + 3) {
          goToSlide(currentSlideRef.current + 1);
        } else {
          goToSlide(0);
        }
      }
      if (touchendX > touchstartX + 150) {
        if (currentSlideRef.current > 0) {
          goToSlide(currentSlideRef.current - 1);
        } else {
          // + 2 compensates for hard coded two slide (supposed to - 1)
          goToSlide(props.launchpadData.length + 2);
        }
      }
    };

    if (props.launchpadData.length > 0) {
      if (carouselContainer) {
        carouselContainer.addEventListener('touchstart', e => {
          touchstartX = e.changedTouches[0].screenX;
        });

        carouselContainer.addEventListener('touchend', e => {
          touchendX = e.changedTouches[0].screenX;
          handleGesture();
        });
      }

      const myfunc = setInterval(function () {
        if (!pauseSlideRef.current) {
          // + 2 compensates for hard coded two slide
          if (currentSlideRef.current + 1 < props.launchpadData.length + 3) {
            setCurrentSlide(currentSlideRef.current + 1);
            currentSlideRef.current += 1;
          } else {
            setCurrentSlide(0);
            currentSlideRef.current = 0;
          }
        }
      }, 8000);
      return () => {
        clearInterval(myfunc);
        window.removeEventListener('touchstart', e => {
          touchstartX = e.changedTouches[0].screenX;
        });
        window.removeEventListener('touchend', e => {
          touchendX = e.changedTouches[0].screenX;
          handleGesture();
        });
      };
    }
  }, [props.launchpadData]);

  useEffect(() => {
    const app = document.querySelector<HTMLElement>('.app-container');

    const initalViewPortWidth = window.innerWidth;

    if (initalViewPortWidth < 1024) {
      setMobileView(true);
    } else {
      setMobileView(false);
    }

    const onResize = () => {
      const viewPortWidth = window.innerWidth;

      if (viewPortWidth < 1024) {
        setMobileView(true);
      } else {
        setMobileView(false);
      }
    };

    if (app) {
      window.addEventListener('resize', onResize, { passive: true });
      return () => {
        window.removeEventListener('resize', onResize);
      };
    }
  }, []);

  const strangeAdditionalSlidesLength =
    props.launchpadData.length + (props.votingData.images.length > 0 ? 4 : 3);

  return (
    <div className={'w-full'} id="carouselContainer">
      <MogiesCarouselSlide
        mobileView={mobileView}
        currentSlide={currentSlide}
      />
      <ProjectCarouselSlide
        mobileView={mobileView}
        currentSlide={currentSlide}
      />
      <StakingCarouselSlide
        mobileView={mobileView}
        currentSlide={currentSlide}
      />
      {props.votingData.images.length > 0 && (
        <VotingCarouselSlide
          currentSlide={currentSlide}
          votingData={props.votingData}
          countdownHeader={countdownHeader}
        />
      )}

      {props.launchpadData.map((entry, index) => {
        return (
          <div
            className={styles.carouselBanner + ' w-full'}
            // + 3 compensates for hard coded two slides
            key={entry.id}
            // key={index + (props.votingData.images.length > 0 ? 4 : 3)}
            style={
              index + (props.votingData.images.length > 0 ? 4 : 3) ===
              currentSlide
                ? { display: 'block' }
                : { display: 'none' }
            }
          >
            <div
              className="w-full h-full flex xl:justify-between justify-center flex-wrap-reverse box-border lg:p-10 lg:py-10 xl:py-12 p-16 relative"
              style={{
                background: `url('${entry.collection?.bannerImageUrl}')`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '50% 50%',
                borderRadius: 16,
              }}
            >
              <div
                className={
                  styles.overlay +
                  ' w-full h-full absolute top-0 left-0 z-0 rounded-lg'
                }
              />
              <div
                className={
                  'lg:w-1/2 w-full flex flex-col items-center lg:items-start box-border z-10 h-auto lg:text-left text-center'
                }
              >
                {!mobileView &&
                new Date(entry.saleDate).getTime() - new Date().getTime() >
                  0 ? (
                  <div
                    className={
                      styles.upcoming +
                      ' w-28 py-1 px-1 mb-8 flex justify-center items-center'
                    }
                  >
                    <img className="w-2 h-2 mr-2" src={circleIcon} alt="dot" />
                    Upcoming
                  </div>
                ) : null}
                <div
                  className={
                    new Date(entry.saleDate).getTime() - new Date().getTime() >
                    0
                      ? styles.collectionName +
                        ' text-5xl xl:text-7xl mb-3 sm:mb-2 font-extrabold'
                      : styles.collectionName +
                        ' text-5xl lg:text-6xl xl:text-8xl mb-3 sm:mb-2 font-extrabold'
                  }
                >
                  {entry.collection?.name}
                </div>
                <div
                  className={
                    styles.description +
                    ' text-sm xl:text-base xl:pr-14 font-bold'
                  }
                >
                  {entry.collection?.description}
                </div>
                <div className="flex items-center gap-2 box-border mt-auto">
                  <div
                    aria-hidden
                    className={
                      styles.learnButton +
                      ' w-32 lg:w-36 xl:w-44 h-10 lg:h-12 text-xs sm:text-sm	font-semibold	flex justify-center items-center cursor-pointer'
                    }
                    onClick={() => goToLaunchpad(index)}
                  >
                    Learn More
                  </div>
                  <div
                    aria-hidden
                    className={
                      styles.viewButton +
                      ' w-32 lg:w-36 xl:w-44 h-10 lg:h-12 text-xs sm:text-sm font-semibold flex justify-center items-center text-center cursor-pointer pl-1'
                    }
                    onClick={() => goToCollection(index)}
                  >
                    View Collection
                  </div>
                  <div
                    aria-hidden
                    className={
                      styles.notification +
                      ' w-10 h-10 lg:w-12 lg:h-12 relative flex justify-center items-center cursor-pointer'
                    }
                    onClick={() =>
                      setModal(
                        SignupModal(
                          undefined,
                          entry.collection?.contractAddress,
                          entry.collection?.network,
                        ),
                      )
                    }
                  >
                    <NotificationsIcon
                      className={styles.bellShake}
                      style={{ fontSize: 28 }}
                    />
                  </div>
                </div>
              </div>
              <div className="md:w-1/2 w-full z-10 flex flex-col justify-center lg:items-end items-center">
                <div
                  className={
                    'lg:w-9/12 md:w-full w-7/12 h-full flex flex-col justify-center items-center'
                  }
                >
                  <img
                    className={styles.logoSquarify}
                    src={
                      entry.collection?.logoImageUrl != null
                        ? entry.collection.logoImageUrl
                        : undefined
                    }
                    alt="logoImg"
                  />
                  <LaunchpadCountDown startDate={new Date(entry.saleDate)} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {props.collectionData.map((entry, index) => {
        return (
          <div
            className={styles.carouselBanner + ' w-full'}
            // + 3 compensates for hard coded two slides
            key={
              index +
              props.launchpadData.length +
              (props.votingData.images.length > 0 ? 4 : 3)
            }
            style={
              index +
                props.launchpadData.length +
                (props.votingData.images.length > 0 ? 4 : 3) ===
              currentSlide
                ? { display: 'block' }
                : { display: 'none' }
            }
          >
            <div
              className="w-full h-full flex xl:justify-between justify-center flex-wrap-reverse box-border lg:p-10 lg:py-10 xl:py-12 p-16 relative"
              style={{
                background: `url('${entry.bannerImageUrl}')`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '50% 50%',
                borderRadius: 16,
              }}
            >
              <div
                className={
                  styles.overlay +
                  ' w-full h-full absolute top-0 left-0 z-0 rounded-lg'
                }
              />
              <div
                className={
                  'lg:w-1/2 w-full flex flex-col items-center lg:items-start box-border z-10 h-auto lg:text-left text-center'
                }
              >
                <div
                  className={
                    styles.collectionName +
                    ' text-5xl xl:text-7xl mb-3 sm:mb-2 font-extrabold'
                  }
                >
                  {entry.name}
                </div>
                <div
                  className={
                    styles.description +
                    ' text-sm xl:text-base xl:pr-14 font-bold'
                  }
                >
                  {entry.description}
                </div>
                <div className="flex items-center gap-2 box-border mt-auto">
                  <div
                    aria-hidden
                    className={
                      styles.learnButton +
                      ' w-32 lg:w-36 xl:w-44 h-10 lg:h-12 text-xs sm:text-sm font-semibold flex justify-center items-center text-center cursor-pointer pl-1'
                    }
                    onClick={() => goToCollection(index, true)}
                  >
                    View Collection
                  </div>
                  <div
                    aria-hidden
                    className={
                      styles.viewButton +
                      ' w-32 lg:w-36 xl:w-44 h-10 lg:h-12 text-xs sm:text-sm	font-semibold	flex justify-center items-center cursor-pointer'
                    }
                    onClick={() => goToMarketplace()}
                  >
                    View Marketplace
                  </div>
                  <div
                    aria-hidden
                    className={
                      styles.notification +
                      ' w-10 h-10 lg:w-12 lg:h-12 relative flex justify-center items-center cursor-pointer'
                    }
                    onClick={() =>
                      setModal(
                        SignupModal(
                          undefined,
                          entry.contractAddress,
                          entry.network,
                        ),
                      )
                    }
                  >
                    <NotificationsIcon
                      className={styles.bellShake}
                      style={{ fontSize: 28 }}
                    />
                  </div>
                </div>
              </div>
              <div className="md:w-1/2 w-full z-10 flex flex-col justify-center lg:items-end items-center">
                <div
                  className={
                    'lg:w-9/12 md:w-full w-7/12 h-full flex flex-col justify-center items-center'
                  }
                >
                  <img
                    className={styles.logoSquarify}
                    src={
                      entry.logoImageUrl != null
                        ? entry.logoImageUrl
                        : undefined
                    }
                    alt="logoImg"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <div className="flex justify-center gap-4 mt-10">
        <div
          aria-hidden
          className={
            0 === currentSlide
              ? styles.tabsSelected + ' w-8 md:w-12 lg:w-16 pointer'
              : styles.tabs + ' w-8 md:w-12 lg:w-16 pointer'
          }
          key={0}
          onClick={() => goToSlide(0)}
        />
        <div
          aria-hidden
          className={
            1 === currentSlide
              ? styles.tabsSelected + ' w-8 md:w-12 lg:w-16 pointer'
              : styles.tabs + ' w-8 md:w-12 lg:w-16 pointer'
          }
          key={1}
          onClick={() => goToSlide(1)}
        />
        <div
          aria-hidden
          className={
            2 === currentSlide
              ? styles.tabsSelected + ' w-8 md:w-12 lg:w-16 pointer'
              : styles.tabs + ' w-8 md:w-12 lg:w-16 pointer'
          }
          key={2}
          onClick={() => goToSlide(2)}
        />
        {props.votingData.images.length > 0 && (
          <div
            aria-hidden
            className={
              3 === currentSlide
                ? styles.tabsSelected + ' w-8 md:w-12 lg:w-16 pointer'
                : styles.tabs + ' w-8 md:w-12 lg:w-16 pointer'
            }
            key={3}
            onClick={() => goToSlide(3)}
          />
        )}
        {props.launchpadData.map((entry, index) => {
          return (
            <div
              aria-hidden
              className={
                index + (props.votingData.images.length > 0 ? 4 : 3) ===
                currentSlide
                  ? styles.tabsSelected + ' w-8 md:w-12 lg:w-16 pointer'
                  : styles.tabs + ' w-8 md:w-12 lg:w-16 pointer'
              }
              key={index + (props.votingData.images.length > 0 ? 4 : 3)}
              onClick={() =>
                goToSlide(index + (props.votingData.images.length > 0 ? 4 : 3))
              }
            />
          );
        })}
        {props.collectionData.map((entry, index) => {
          return (
            <div
              aria-hidden
              className={
                index + strangeAdditionalSlidesLength === currentSlide
                  ? styles.tabsSelected + ' w-8 md:w-12 lg:w-16 pointer'
                  : styles.tabs + ' w-8 md:w-12 lg:w-16 pointer'
              }
              key={index + strangeAdditionalSlidesLength}
              onClick={() => goToSlide(index + strangeAdditionalSlidesLength)}
            />
          );
        })}
      </div>
      <div className="mt-2 w-full flex justify-between">
        <IconButton
          onClick={() =>
            goToSlide(
              currentSlide - 1 >= 0
                ? currentSlide - 1
                : props.collectionData.length +
                    strangeAdditionalSlidesLength -
                    1,
            )
          }
          color="primary"
        >
          <ArrowBack />
        </IconButton>
        <IconButton
          onClick={() =>
            goToSlide(
              currentSlide + 1 <
                props.collectionData.length + strangeAdditionalSlidesLength
                ? currentSlide + 1
                : 0,
            )
          }
          color="primary"
        >
          <ArrowForward />
        </IconButton>
      </div>
    </div>
  );
};
